import { Trello } from "./types/trello"

// ─── Keys ────────────────────────────────────────────────────────────────────

const FORCE_RENDER_KEY = "render"
const CARD_CUSTOMER_KEY = "qbCustomer"

// Every write in here is best-effort. Trello's commands travel over postMessage
// and fail with "Failed to fetch" when the iframe is torn down or the tab wakes
// from sleep with no network — neither of which is worth failing an operation
// over, or reporting.
const bestEffort = async (write: PromiseLike<void>) => {
  try {
    await write
  } catch (e) {
    console.warn("Could not update the QuickBooks customer card badge", e)
  }
}

// ─── Force card back re-render ───────────────────────────────────────────────

// A nudge, not a write anyone depends on: the API holds the link, and the card
// back re-reads it when opened. Unguarded, a failed nudge surfaced as an
// unhandled rejection — "Failed to fetch (Command: set)" — from a tab that had
// simply been left open overnight.
export const forceCardBackRender = (t: Trello.PowerUp.IFrame) =>
  bestEffort(t.set("member", "private", FORCE_RENDER_KEY, Math.random().toString()))

// ─── Linked customer, mirrored into Trello shared card data ──────────────────
//
// The link itself lives in the API (CardCustomerLink). This is a denormalized
// copy so `card-badges` can render on the board without one API round trip per
// card — badge callbacks run for every visible card, so they must read local
// data only. Written on link/unlink and backfilled by the card back whenever it
// resolves a name (cards linked before badges existed have no copy yet).

export interface StoredCardCustomer {
  id: string
  name: string
  // The card this copy was written for. Trello duplicates shared card data when
  // a card is copied, but the API-side link is keyed by card id and is not
  // copied — so a mismatch means this copy belongs to the original card and must
  // not be trusted. Absent on copies written before this field existed.
  cardId?: string
}

// The API (CardCustomerLink) is the source of truth and this is only a cache for
// the badge, so a failed write must never fail the operation that triggered it:
// members without card-write access — Trello observers, or anyone on a board
// they don't belong to — will have these rejected, and the card back writes on
// every open.

export const getStoredCardCustomer = async (
  t: Trello.PowerUp.IFrame
): Promise<StoredCardCustomer | undefined> => {
  try {
    return await t.get("card", "shared", CARD_CUSTOMER_KEY)
  } catch {
    return undefined
  }
}

export const setStoredCardCustomer = (
  t: Trello.PowerUp.IFrame,
  customer: StoredCardCustomer
) => bestEffort(t.set("card", "shared", CARD_CUSTOMER_KEY, customer))

export const clearStoredCardCustomer = (t: Trello.PowerUp.IFrame) =>
  bestEffort(t.remove("card", "shared", CARD_CUSTOMER_KEY))

/**
 * Drop the mirrored customer from every card on the board. Used by `remove-data`
 * so uninstalling the power-up doesn't leave QuickBooks customer names behind in
 * Trello. Scoping a write by card id (rather than "card") targets a card other
 * than the one in context.
 */
export const clearAllStoredCardCustomers = async (t: Trello.PowerUp.IFrame) => {
  try {
    const cards = await t.cards("id")
    await Promise.all(
      cards.map((card) => bestEffort(t.remove(card.id, "shared", CARD_CUSTOMER_KEY)))
    )
  } catch (e) {
    console.warn("Could not clear QuickBooks customer card badges", e)
  }
}
